import { supabaseClient } from './supabase';
import { toast } from './toast';

interface CandyContentResponse {
  response: {
    current_balance: number;
    matches_sites: Record<string, string>;
    old_balance: number;
    price: number;
    uniqueness_text: string;
  } | string | number;
}

const API_URL = 'https://candy-content.com/api/check-uniqueness/';

async function getApiKey(): Promise<string> {
  const { data, error } = await supabaseClient
    .from('api_services')
    .select('api_key, is_active')
    .eq('service_type', 'candycontent')
    .single();

  if (error) throw new Error('Failed to fetch CandyContent API key');
  if (!data) throw new Error('CandyContent API service not found');
  if (!data.is_active) throw new Error('CandyContent API service is not active');

  return data.api_key;
}

export async function checkUniqueness(text: string, url?: string | null): Promise<number> {
  const loadingToast = toast.loading('Checking text uniqueness...');

  try {
    if (!text.trim()) {
      toast.error('Text is empty', { id: loadingToast });
      throw new Error('Text is empty');
    }

    const cleanedText = text.trim()
      .replace(/[\r\n]+/g, '\n')
      .replace(/\n\s*\n/g, '\n')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleanedText.length < 100) {
      toast.error('Text must be at least 100 characters long', { id: loadingToast });
      throw new Error('Text must be at least 100 characters long');
    }

    const apiKey = await getApiKey();

    const formData = new URLSearchParams();
    formData.append('api_key', apiKey);
    formData.append('text', cleanedText);
    formData.append('precision', 'better');

    try {
      console.log('Sending request to CandyContent API...');
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        body: formData.toString()
      });

      console.log('CandyContent API Response Status:', response.status);
      
      const responseText = await response.text();
      console.log('CandyContent API Raw Response:', responseText);

      // If response is empty or invalid JSON, try to extract percentage from status
      if (!responseText.trim() || responseText === '200 (OK)') {
        const matches = response.statusText.match(/(\d+)/);
        if (matches && matches[1]) {
          const percentage = parseInt(matches[1], 10);
          if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
            toast.success('Uniqueness check completed', { id: loadingToast });
            return percentage;
          }
        }
      }

      // Try to parse JSON response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing JSON response:', parseError);
        toast.error('Invalid response from API', { id: loadingToast });
        throw new Error('Invalid JSON response from API');
      }

      console.log('CandyContent API Parsed Response:', data);

      // Handle different response formats
      if (typeof data === 'number') {
        toast.success('Uniqueness check completed', { id: loadingToast });
        return data;
      }

      if (data && typeof data === 'object') {
        if (typeof data.response === 'number') {
          toast.success('Uniqueness check completed', { id: loadingToast });
          return data.response;
        }

        if (typeof data.response === 'object' && data.response.uniqueness_text) {
          const percentage = parseInt(data.response.uniqueness_text.replace('%', ''), 10);
          if (!isNaN(percentage)) {
            toast.success('Uniqueness check completed', { id: loadingToast });
            return percentage;
          }
        }
      }

      toast.error('Unexpected API response format', { id: loadingToast });
      throw new Error('Unexpected API response format');

    } catch (fetchError) {
      console.error('CandyContent API Error:', fetchError);
      toast.error(fetchError.message || 'Failed to check uniqueness', { id: loadingToast });
      throw fetchError;
    }

  } catch (error) {
    console.error('Error checking uniqueness:', error);
    // Ensure the loading toast is dismissed with an error message
    toast.error(error instanceof Error ? error.message : 'Failed to check uniqueness', { id: loadingToast });
    throw error;
  }
}